import { useState, useEffect } from 'react';
import { AnimatedKanbanHero } from './AnimatedKanbanHero';
import { Sparkles, Wand2, Lightbulb, Hash, TrendingUp, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// ─── AI Copilot Modal Overlay ─────────────────────────────────────────────────
function AICopilotModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(t);
  }, []);

  const fade = (delay: number) => ({
    opacity: show ? 1 : 0,
    transform: show ? 'translateY(0)' : 'translateY(8px)',
    transition: `opacity 0.35s ease ${delay}s, transform 0.35s ease ${delay}s`,
  });

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/25 backdrop-blur-[2px] rounded-2xl"
      onClick={onClose}
    >
      <div
        className="w-[52%] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ animation: 'aiModalIn 0.4s cubic-bezier(0.34,1.56,0.64,1) forwards' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-pink-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            </div>
            <span className="text-xs font-bold text-gray-900 dark:text-white">{t('landing.mockup.createCard')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-gray-300" />
            <div className="w-2 h-2 rounded-full bg-gray-300" />
            <button onClick={onClose} className="p-0.5 hover:bg-gray-100 rounded-md">
              <X className="w-3 h-3 text-gray-400" />
            </button>
          </div>
        </div>

        <div className="p-3 space-y-2.5 max-h-[68vh] overflow-y-auto">

          {/* Content Score */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-2.5" style={fade(0.05)}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-3 h-3 text-gray-400" />
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">{t('landing.mockup.contentScore')}</span>
              </div>
              <span className="text-[10px] font-bold text-green-600 border border-green-200 bg-green-50 px-2 py-0.5 rounded-full">78 / 100</span>
            </div>
            <div className="grid grid-cols-4 gap-1 text-center">
              {[{ l: 'TITLE', v: 85, c: 'text-green-600' }, { l: 'DESC', v: 70, c: 'text-yellow-500' }, { l: 'TAGS', v: 70, c: 'text-yellow-500' }, { l: 'SEO', v: 75, c: 'text-green-600' }].map(s => (
                <div key={s.l} className="bg-gray-50 dark:bg-gray-800 rounded-lg py-1.5">
                  <p className="text-[7px] text-gray-400 uppercase tracking-wide">{s.l}</p>
                  <p className={`text-xs font-bold ${s.c}`}>{s.v}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Topic / Hook */}
          <div style={fade(0.12)}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider">{t('landing.mockup.topicHook')}</span>
              <span className="text-[10px] font-bold text-green-500">85</span>
            </div>
            <div className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 rounded-xl px-2.5 py-1.5">
              <p className="text-[9px] text-gray-700 dark:text-gray-300 flex-1">5 MacBook Silicon Hacks Every New User Must Know!</p>
              <button className="flex items-center gap-1 bg-indigo-600 text-white text-[8px] font-semibold px-2 py-1 rounded-lg flex-shrink-0">
                <Wand2 className="w-2 h-2" />{t('card.generate')}</button>
            </div>
          </div>

          {/* Caption Body */}
          <div style={fade(0.2)}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider">{t('landing.mockup.captionBody')}</span>
              <span className="text-[8px] text-gray-400">200 / 2200</span>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-2 min-h-[44px]">
              <p className="text-[9px] text-gray-600 dark:text-gray-400 leading-relaxed">Share quick, lesser-known tips that enhance MacBook Silicon performance and user experience, perfect for new users...</p>
              <div className="flex justify-end mt-1">
                <button className="flex items-center gap-1 bg-indigo-600 text-white text-[8px] font-semibold px-2 py-1 rounded-lg">
                  <Wand2 className="w-2 h-2" />{t('card.generate')}</button>
              </div>
            </div>
          </div>

          {/* Strategy Insight */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-xl p-2" style={fade(0.28)}>
            <div className="flex items-center gap-1.5 mb-1">
              <Lightbulb className="w-3 h-3 text-amber-600" />
              <span className="text-[8px] font-bold text-amber-700 uppercase tracking-wider">{t('landing.mockup.strategyInsight')}</span>
            </div>
            <p className="text-[8px] text-amber-700 dark:text-amber-300 leading-relaxed">Reels with concise, valuable tips perform well as users look for quick solutions. Trending tech tips attract new MacBook users eager to optimize their devices.</p>
          </div>

          {/* Hashtags */}
          <div style={fade(0.35)}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <Hash className="w-3 h-3 text-gray-400" />
                <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wider">{t('landing.mockup.hashtags')}</span>
                <span className="text-[7px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-1 py-0.5 rounded-full">8/30</span>
              </div>
              <span className="text-[10px] font-bold text-yellow-500">70</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {[
                { tag: '#MacBookTips', v: 90 }, { tag: '#AppleSilicon', v: 85 },
                { tag: '#TechHacks', v: 75 },   { tag: '#MacBook', v: 80 },
              ].map(h => (
                <span key={h.tag} className="text-[7px] font-semibold px-1.5 py-0.5 rounded-md bg-green-50 text-green-700 border border-green-200">
                  {h.tag} <span className="opacity-60">{h.v}</span>
                </span>
              ))}
            </div>
          </div>

          {/* AI Active Badge */}
          <div className="flex items-center justify-center gap-2 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800/50" style={fade(0.42)}>
            <Sparkles className="w-3 h-3 text-indigo-600" />
            <span className="text-[9px] font-bold text-indigo-700 dark:text-indigo-300">{t('landing.mockup.copilotActive')}</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes aiModalIn {
          from { opacity: 0; transform: scale(0.9) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export function HeroProductMockup() {
  const [showModal, setShowModal] = useState(false);

  // Open modal ~2.4s after mount (right after the first fast drag animation), then loop
  useEffect(() => {
    let openTimer: ReturnType<typeof setTimeout>;
    let closeTimer: ReturnType<typeof setTimeout>;

    const schedule = (openDelay: number) => {
      openTimer = setTimeout(() => {
        setShowModal(true);
        closeTimer = setTimeout(() => {
          setShowModal(false);
          // Restart loop: show modal again after another full drag cycle (3200ms wait)
          schedule(3200);
        }, 4000);
      }, openDelay);
    };

    schedule(2400);

    return () => {
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
    };
  }, []);

  return (
    <div className="relative w-full h-full max-w-[1426px] mx-auto select-none">
      {/* Clipping wrapper — height matches visual kanban height */}
      <div className="relative overflow-hidden rounded-2xl h-full">
        <AnimatedKanbanHero />
        {showModal && (
          <AICopilotModal onClose={() => setShowModal(false)} />
        )}
      </div>

      {/* Ambient glow — outside the clip so it bleeds naturally */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-gradient-to-r from-primary-500/10 via-purple-500/10 to-pink-500/10 rounded-full blur-3xl -z-10 pointer-events-none" />
    </div>
  );
}
